import React, { useMemo, useState, useRef } from 'react';
import { useTranslation } from "react-i18next";
import { AlertCircle, AlertTriangle, ExternalLink } from 'lucide-react';
import { useBoardChartData } from '../../../hooks/useBoardChartData';
import { SourceIcon } from './SourceIcon';
import { calculateRevenueSourceBreakdown } from './revenueSourceBreakdown';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getSubSourceSegmentColor } from './revenueSubSourceBreakdownColors';

interface RevenueSubSourceBreakdownProps {
    link?: any; // Pre-calculated link (legacy / performance)
    entityId?: string; // Single entity ID
    entityIds?: string[]; // Multiple entity IDs
    onOpenInfoModal?: (title: string, description: string) => void;
}

/**
 * Helper to construct an external link for an entity.
 */
const constructEntityLink = (match: any) => {
    if (match.originUrls && match.originUrls.length > 0) {
        return match.originUrls[match.originUrls.length - 1];
    }
    if (match.platformUrl) return match.platformUrl;
    const firstUrl = match.sourceUrls?.[0];
    if (firstUrl && (firstUrl.startsWith('http://') || firstUrl.startsWith('https://'))) {
        return firstUrl;
    }
    return null;
};

/**
 * Internal Connectivity Status Bar component - moved from TrafficPage.
 */
const ConnectivityStatusBar = ({
    link,
    t,
    onOpenInfoModal,
    onOpenBoardContent,
    onOpenConnectModal,
    onOpenEntityDetails
}: {
    link: any;
    t: any;
    onOpenInfoModal?: (title: string, description: string) => void;
    onOpenBoardContent: () => void;
    onOpenConnectModal: () => void;
    onOpenEntityDetails: (channelId: string, id: string) => void;
}) => {
    const [hoveredId, setHoveredId] = useState<string | null>(null);
    const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const handleMouseEnter = (id: string) => {
        if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
        setHoveredId(id);
    };

    const handleMouseLeave = () => {
        hideTimeoutRef.current = setTimeout(() => {
            setHoveredId(null);
        }, 150);
    };

    const sortedMatches = [...link.matches].sort((a, b) => b.revenue - a.revenue);

    return (
        <div className="flex flex-col items-center w-full px-2 relative">
            <div className="h-2.5 w-full bg-neutral-100 dark:bg-neutral-800 rounded-full flex shadow-inner relative border border-neutral-200/50 dark:border-neutral-700/50">
                <div className="absolute inset-0 flex rounded-full overflow-hidden pointer-events-none">
                    {sortedMatches.map((match: any, idx: number) => {
                        const width = Math.max(0.5, (match.revenue / Math.max(1, link.revenue)) * 100);
                        const isActive = match.isSelected && match.isEnabled && match.isMatched && !match.isDisconnected;
                        const color = getSubSourceSegmentColor(idx, { isOrganic: !!match.isOrganic, isActive });

                        return (
                            <div
                                key={`bar-${match.id}`}
                                style={{ width: `${width}%`, backgroundColor: color }}
                                className="h-full border-r border-white/20 last:border-0"
                            />
                        );
                    })}
                </div>

                <div className="absolute inset-0 flex items-stretch -top-1 -bottom-1">
                    {sortedMatches.map((match: any, idx: number, sortedArr: any[]) => {
                        const width = Math.max(0.5, (match.revenue / Math.max(1, link.revenue)) * 100);
                        const isHovered = hoveredId === match.id;
                        const isActive = match.isSelected && match.isEnabled && match.isMatched && !match.isDisconnected;
                        // const color = getSubSourceSegmentColor(idx, { isOrganic: !!match.isOrganic, isActive });

                        let prevCumulative = 0;
                        for (let i = 0; i < idx; i++) {
                            prevCumulative += Math.max(0.5, (sortedArr[i].revenue / Math.max(1, link.revenue)) * 100);
                        }
                        const center = prevCumulative + width / 2;
                        let shift = -50;
                        if (center < 15) shift = -50 + (15 - center) * 3;
                        else if (center > 85) shift = -50 - (center - 85) * 3;
                        const finalShift = Math.max(-100, Math.min(0, shift));

                        return (
                            <div
                                key={`trigger-${match.id}`}
                                style={{ width: `${width}%` }}
                                className="h-full relative cursor-pointer"
                                onMouseEnter={() => handleMouseEnter(match.id)}
                                onMouseLeave={handleMouseLeave}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setHoveredId(match.id);
                                }}
                            >
                                {isHovered && (
                                    <div
                                        className="absolute bottom-full left-1/2 z-[100] scale-100 transition-all cursor-default mb-2"
                                        style={{ transform: `translateX(${finalShift}%)` }}
                                        onMouseEnter={() => handleMouseEnter(match.id)}
                                        onMouseLeave={handleMouseLeave}
                                    >
                                        <div className="absolute top-full left-0 right-0 h-4" />

                                        {(() => {
                                            const url = constructEntityLink(match);
                                            const hasLink = !!url;

                                            return (
                                                <div
                                                    className="bg-neutral-900 dark:bg-neutral-100 text-white dark:text-neutral-900 text-[9px] font-black uppercase tracking-[0.2em] px-4 py-2 rounded shadow-2xl border border-white/20 dark:border-black/10 flex items-center gap-1 whitespace-nowrap transition-all cursor-pointer hover:brightness-110 active:scale-95"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (match.isOrganic) {
                                                            onOpenInfoModal?.(
                                                                t('pages.traffic.modals.organicTitle', { defaultValue: 'Direct & Organic Attribution' }),
                                                                t('pages.traffic.modals.organicDescription', { defaultValue: 'This revenue is attributed to direct visits or non-paid search results.' })
                                                            );
                                                        } else if (match.isDisconnected) {
                                                            onOpenBoardContent();
                                                        } else if (match.channelId && match.isMatched) {
                                                            onOpenEntityDetails(match.channelId, match.id);
                                                        } else if (!match.isMatched) {
                                                            onOpenConnectModal();
                                                        }
                                                    }}
                                                >
                                                    {(() => {
                                                        const channelName = match.channelName || t('pages.traffic.modals.unknownChannel', { defaultValue: 'Unknown' });
                                                        return (
                                                                        <>
                                                    {match.isOrganic || (isActive && hasLink) ? (
                                                         <SourceIcon name={link.source} className="w-2.5 h-2.5" />
                                                    ) : (match.isDisconnected || (match.isMatched && !match.isSelected)) ? (
                                                                    <AlertTriangle className={`w-2.5 h-2.5 ${match.isDisconnected ? 'text-rose-500' : 'text-amber-500'}`} />
                                                                ) : isActive && !hasLink ? (
                                                                    <AlertTriangle className="w-2.5 h-2.5 text-rose-500 animate-pulse" />
                                                                ) : (
                                                                    <AlertCircle className="w-2.5 h-2.5 text-rose-500" />
                                                                )}
                                                                <span className={(isActive && hasLink) || match.isOrganic ? '' : match.isDisconnected ? 'text-rose-500' : (match.isMatched && !match.isSelected) ? 'text-amber-500' : 'text-rose-500'}>
                                                                    {match.isDisconnected ? `${channelName} ${t('pages.traffic.modals.disconnectedLabel', { defaultValue: 'DISCONNECTED' })}: ` : match.isMatched ? `${match.name}: ` : ''}
                                                                    {((match.revenue / Math.max(1, link.revenue)) * 100).toFixed(1)}%
                                                                    {!match.isOrganic && !isActive && !match.isDisconnected && (
                                                                        match.isMatched
                                                                            ? ` - ${t('pages.traffic.modals.untrackedLabel', { defaultValue: 'UNTRACKED' })}`
                                                                            : ` - ${t('pages.traffic.modals.unknownSourceLabel', { defaultValue: 'UNKNOWN SOURCE' })}`
                                                                    )}
                                                                    {isActive && !hasLink && ' - (!)'}
                                                                </span>
                                                                {isActive && hasLink && <ExternalLink className="w-2.5 h-2.5 opacity-50 ml-1" />}
                                                            </>
                                                        );
                                                    })()}
                                                </div>
                                            );
                                        })()}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

/**
 * Generic RevenueSubSourceBreakdown component.
 * It can accept a pre-calculated link OR an entityId/entityIds to calculate its own data.
 */
export const RevenueSubSourceBreakdown: React.FC<RevenueSubSourceBreakdownProps> = ({
    link,
    entityId,
    entityIds,
    onOpenInfoModal
}) => {
    const { t } = useTranslation("common");
    const { finance, entities, channels, dateRange } = useBoardChartData();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    // 1. Logic for actions
    const handleOpenConnectModal = () => {
        const params = new URLSearchParams(searchParams);
        params.set('openModal', 'select_channel_source');
        navigate(`?${params.toString()}`, { replace: true });
    };

    const handleOpenBoardContent = () => {
        const params = new URLSearchParams(searchParams);
        params.set('openContent', 'true');
        navigate(`?${params.toString()}`, { replace: true });
    };

    const handleOpenEntityDetails = (channelId: string, entityId: string) => {
        const params = new URLSearchParams(searchParams);
        params.set('tab', 'channels');
        params.set('channelId', channelId);
        params.set('entityId', entityId);
        params.set('highlight', 'true');
        navigate(`?${params.toString()}`, { replace: true });
    };

    // 2. Data calculation if link is not provided.
    // Computed unconditionally to comply with rules-of-hooks; bails out early
    // when no entityId/entityIds (and unused entirely when `link` is provided).
    const calculatedMappings = useMemo(() => {
        if (!entityId && (!entityIds || entityIds.length === 0)) return [];
        return calculateRevenueSourceBreakdown(finance, entities, channels, dateRange, t, entityId);
    }, [finance, entities, channels, dateRange, entityId, entityIds, t]);

    // If a single link IS provided, we just render that bar.
    if (link) {
        return (
            <ConnectivityStatusBar
                link={link}
                t={t}
                onOpenInfoModal={onOpenInfoModal}
                onOpenBoardContent={handleOpenBoardContent}
                onOpenConnectModal={handleOpenConnectModal}
                onOpenEntityDetails={handleOpenEntityDetails}
            />
        );
    }

    // Generic mode: render a list of sources with bars for the entity/entities.
    if (calculatedMappings.length === 0) return null;

    // For a single metric (entity), we show all its links.
    const activeMetric = calculatedMappings[0]; // If entityId was passed, there will be one main group

    return (
        <div className="space-y-4">
            {activeMetric.links.map((link: any, idx: number) => (
                <div key={idx} className="space-y-1">
                    <div className="flex items-center justify-between px-2">
                        <div className="flex items-center gap-1">
                            <SourceIcon name={link.source} className="w-[10px] h-[10px]" />
                            <span className="text-[8px] font-bold text-neutral-500">{link.source}</span>
                        </div>
                        <span className="text-[10px] font-black text-neutral-900 dark:text-neutral-100">
                             {((link.revenue / activeMetric.totalRevenue) * 100).toFixed(1)}%
                        </span>
                    </div>
                    <ConnectivityStatusBar
                        link={link}
                        t={t}
                        onOpenInfoModal={onOpenInfoModal}
                        onOpenBoardContent={handleOpenBoardContent}
                        onOpenConnectModal={handleOpenConnectModal}
                        onOpenEntityDetails={handleOpenEntityDetails}
                    />
                </div>
            ))}
        </div>
    );
};
